// FCM registration + token persistence matching the Cloud Function schema.
// `functions/main.py::_get_staff_fcm_tokens` reads tokens from the
// `clubs/{clubId}/staff/{staffDocId}.devices` map (one entry per device,
// keyed by a stable localStorage device id). Onboarding always creates an
// owner staff doc keyed by the owner's UID, so every push recipient has a
// staff doc to write to.

import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/core/firebase/client'
import { clubSubcollections } from '@/core/firebase/paths'
import { logger } from '@/core/logger'
import { registerFcmDevice } from './fcm'
import { getDeviceId, getDeviceLabel } from './device-id'

/**
 * Look up the staff document for the current user inside a club. Returns the
 * doc ID or null if the user has no staff entry (e.g., owner-only without an
 * assigned staff doc — see the implementation for the same fallback).
 */
async function findStaffDocId(clubId: string, uid: string): Promise<string | null> {
  try {
    const q = query(
      collection(db, clubSubcollections.staff(clubId)),
      where('uid', '==', uid),
    )
    const snap = await getDocs(q)
    const first = snap.docs[0]
    return first?.id ?? null
  } catch (err) {
    // `permission-denied` here is a benign auth-transition: the FCM auto-
    // register fires from a useEffect whose deps land before the Firestore
    // SDK's internal auth token has fully synced (post-sign-in tick / HMR
    // remount). The rule requires `isClubMember`, which a real member
    // passes once the token settles. There's no recoverable action — the
    // token will be persisted on the next ensureFcmRegistration call from
    // the post-auth retry chain. Demoting to debug keeps Sentry clean
    // (M2 from the 2026-05-23 verification).
    if ((err as { code?: string })?.code === 'permission-denied') {
      logger.d('staff doc lookup: permission-denied (auth in flight)', err)
    } else {
      logger.w('staff doc lookup failed', err)
    }
    return null
  }
}

/**
 * Persist the FCM token to the right Firestore location so the Cloud
 * Function can target this device. Writes this device's entry in the
 * `devices` map (`devices.{deviceId}.{token,label,lastSeen}`) — never
 * `enabled`, so a user's per-device opt-out survives re-sign-in.
 * Safe to call every sign-in — writes are idempotent.
 */
async function persistFcmToken(
  clubId: string,
  uid: string,
  token: string,
): Promise<void> {
  // Write this device's entry in the `devices` map. Deliberately does NOT set
  // `enabled` — absent means on, and if the user previously toggled THIS
  // device off we must not silently flip it back on at the next sign-in.
  const deviceId = getDeviceId()
  const patch = {
    [`devices.${deviceId}.token`]: token,
    [`devices.${deviceId}.label`]: getDeviceLabel(),
    [`devices.${deviceId}.lastSeen`]: serverTimestamp(),
  }
  try {
    const staffId = await findStaffDocId(clubId, uid)
    if (!staffId) {
      // Expected for platform-admin sessions and pre-claim mockup viewers
      // — neither has a staff doc on the current club, and that's fine.
      // Logged at debug (not info) because Sentry's React-SDK breadcrumb
      // integration was somehow promoting the info-level console message
      // to a Sentry event in dev (issue regressed as CLUBMANAGER-PWA-1E,
      // 80 events). Debug is gated off in prod and not captured anywhere,
      // so the channel is fully silent.
      logger.d('fcm token skipped: no staff doc for current user')
      return
    }
    await updateDoc(doc(db, clubSubcollections.staff(clubId), staffId), patch)
    logger.i('fcm token stored on staff doc')
  } catch (err) {
    // Don't block sign-in on a token write failure — push is nice-to-have.
    logger.w('fcm token persist failed', err)
  }
}

export interface EnsureFcmRegistrationInput {
  clubId: string
  uid: string
  vapidKey: string
}

/**
 * Full sign-in FCM flow. Requests permission, registers the SW, grabs a
 * token, writes it to Firestore at the path the existing Cloud Function
 * reads from. Returns the token (or null when the user declined / the
 * browser can't do push).
 */
export async function ensureFcmRegistration(
  input: EnsureFcmRegistrationInput,
): Promise<string | null> {
  const { token, permission } = await registerFcmDevice({ vapidKey: input.vapidKey })
  if (permission !== 'granted' || !token) {
    logger.i(`fcm permission: ${permission} (no token)`)
    return null
  }
  await persistFcmToken(input.clubId, input.uid, token)
  return token
}
